import type { Subset } from '@payaca/utilities/types';
import type { EntityType } from './entity-type';
import type { TimestampedEntity } from './timestampedEntityTypes';

/**
 * The maximum size a file upload can be if uploaded via this application,
 * not directly to GCS from the client.
 */
export const UPLOAD_MAX_FILESIZE_LIMIT_VIA_APP = 30 * 1024 * 1024; // 30MB

export interface Upload extends TimestampedEntity {
  id: number;
  publicId: string;
  accountId?: number;
  url: string;
  thumbnailUrl?: string;
  fileName: string;
  fileExtension: string;
  mimeType: string;
  fileSize: number; // bytes
  isSystemManaged: boolean;
  metadata?: UploadMetadata;
}

export type UploadMetadata = {
  source:
    | {
        type: 'projectIntegration';
        service: 'EASY_PV' | 'HEATPUNK' | 'OPEN_SOLAR';
        projectIntegrationId: number;
        fileId?: string;
      }
    | {
        type: 'productSpecScrape';
        url: string;
      };
};

export interface UploadAttachableEntity {
  uploadIds: number[];
}

/**
 * Declared as an array so the union below cannot drift from it, and an entity
 * type read back from persisted JSON can be narrowed at runtime.
 */
export const UPLOAD_ATTACHABLE_ENTITY_TYPES = [
  'account',
  'automationAction',
  'changeProposal',
  'cityPlumbingProduct',
  'deal',
  'documentForm',
  'invoice',
  'material',
  'purchaseOrder',
  'scheduledEvent',
  'timelog',
  'user',
  'job',
  'servicePlan',
] as const satisfies readonly EntityType[];

export type UploadAttachableEntityType = Subset<
  EntityType,
  (typeof UPLOAD_ATTACHABLE_ENTITY_TYPES)[number]
>;

export const isUploadAttachableEntityType = (
  value: string
): value is UploadAttachableEntityType =>
  UPLOAD_ATTACHABLE_ENTITY_TYPES.includes(value as UploadAttachableEntityType);

export type ImageEditConfig = {
  crop: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  resize: { width: number; height: number };
};
